/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import { createContextRoute } from '@gh/common-internal-shared';
import { ActionFlow, getRouteInitData, resetPrivilegedTokensAndUpdate } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { SystemVarsEstimating } from '../../resources/model/system-vars-estimating.model';
import { SystemVarsEstimatingBusinessService } from '../../resources/system-vars-estimating-business.service';
import { SystemVarsEstimatingCommonSubTabComponent } from './system-vars-estimating-common-subtab/system-vars-estimating-common-subtab.component';
import { SystemVarsEstimatingCommonSubTabFormState } from './system-vars-estimating-common-subtab/system-vars-estimating-common-subtab.form.service';
import { SystemVarsEstimatingOrdersSubTabComponent } from './system-vars-estimating-orders-subtab/system-vars-estimating-orders-subtab.component';
import { SystemVarsEstimatingOrderSubTabFormState } from './system-vars-estimating-orders-subtab/system-vars-estimating-orders-subtab.form.service';
import { SystemVarsEstimatingTabFormState } from './system-vars-estimating-tab.form.service';

@Component({
  selector: 'gh-system-vars-estimating-tab',
  template: require('./system-vars-estimating-tab.component.html'),
  providers: [
    SystemVarsEstimatingTabFormState,
    SystemVarsEstimatingCommonSubTabFormState,
    SystemVarsEstimatingOrderSubTabFormState,
  ],
})
export class SystemVarsEstimatingTabComponent extends AbstractFormComponent<SystemVarsEstimating> implements OnInit {
  currentTab: string = 'common';
  sysVarsEstimating: SystemVarsEstimating;

  routes = [
    createContextRoute({
      component: SystemVarsEstimatingCommonSubTabComponent,
      path: 'common',
      init: {
        service: SystemVarsEstimatingBusinessService,
        selector: 'initCommonSettingsTabView',
      },
    }),
    createContextRoute({
      component: SystemVarsEstimatingOrdersSubTabComponent,
      path: 'orders',
      init: {
        service: SystemVarsEstimatingBusinessService,
        selector: 'initOrdersSettingsTabView',
      },
    }),
  ];

  constructor(public form: SystemVarsEstimatingTabFormState,
              private pipelines: FormActionPipelines,
              private actions: CommonActions,
              private navigationService: BuilderNavigationService,
              private route: ActivatedRoute,
              private businessService: SystemVarsEstimatingBusinessService,
              private dialogService: DialogService) {
    super();
  }

  ngOnInit(): void {
    const { sysVarsEstimating } = getRouteInitData(this.route);
    assign(this, { sysVarsEstimating });

    this.form.build(sysVarsEstimating);
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsEstimating>> {
    return this.pipelines.complete({
      validateForm: this.actions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.actions.execute((state: FormFlowState<SystemVarsEstimating>) =>
        this.businessService.update(state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsEstimating>) => {
        const { value } = state;
        this.sysVarsEstimating = value;
        this.form.build(value);
      },
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        resetPrivilegedTokensAndUpdate(this.form);
        this.form.build(this.sysVarsEstimating);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedEstimating],
      label: label ? label : 'lbl_menu_estimating',
    });
  }

  onTabChanged(tabName: string): void {
    this.currentTab = tabName;
  }

  private handleErrors(): void {
    const invalidForm = this.form.findChildForm((form) => form.invalid);
    if (invalidForm) {
      invalidForm.form.markAllAsTouched();
      this.currentTab = invalidForm.name;
    }
  }
}
